import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useRegisterMutation } from '../../app/services/validDocuments';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useTranslation } from 'react-i18next';
import { commonTextFieldStyles, commonTextAreaStyles } from '../../utils/textFieldStyles';
import DragDropFileUpload from '../DragDropFileUpload';
import ModalComponent from '../ModalComponent';
import ConfirmDiscardDialog from '../ConfirmDiscardDialog';
import { useUnsavedChangesWarning } from '../../hooks/useUnsavedChangesWarning';

import {
  Alert,
  Box,
  Button,
  Checkbox,
  Divider,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Stack,
  TextField,
  Typography,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { ModalButton, ModalDialogActions } from '../ModalComponents';

import styles from '../ModalComponent/modalStyles.module.css';
import 'dayjs/locale/de';
import dayjs from 'dayjs';
import { validateFormData, getValidationErrorMessage } from '../../helpers';
import { postValidationDocSchema } from '../../schemas/postValidationDocSchema';
import { INPUT_FILE_ACCEPT_DOC } from '../../constants';

const initialForm = {
  name: '',
  description: '',
  type: '',
};

const VALIDATION_TYPES = ['iq', 'oq', 'pq', 'revalidation'];

export default function ModalAddValidDoc({ id, fetchData }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [errorResponse, setErrorResponse] = useState('');
  const [registerValidDocument, { isLoading, error }] = useRegisterMutation();
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [expiresAt, setExpiresAt] = useState(null);
  const [neverExpires, setNeverExpires] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  const dispatch = useDispatch();
  const { t } = useTranslation();

  const [formValidationErrors, setFormValidationErrors] = useState(null);
  const [expiresAtError, setExpiresAtError] = useState('');
  const nameError = getValidationErrorMessage(formValidationErrors, 'name');
  const descrError = getValidationErrorMessage(formValidationErrors, 'description');
  const dateError = getValidationErrorMessage(formValidationErrors, 'date');
  const fileError = getValidationErrorMessage(formValidationErrors, 'file');

  const { guardClose, confirmProps } = useUnsavedChangesWarning(isDirty, open);

  const handleOpen = () => setOpen(true);
  const doClose = () => {
    setOpen(false);
    setForm(initialForm);
    setSelectedDate(null);
    setExpiresAt(null);
    setNeverExpires(false);
    setSelectedFile(null);
    setFormValidationErrors(null);
    setExpiresAtError('');
    setIsDirty(false);
  };

  const applyExpiryPreset = (years) => {
    setIsDirty(true);
    setExpiresAtError('');
    setNeverExpires(false);
    const base = selectedDate || dayjs();
    setExpiresAt(dayjs(base).add(years, 'year'));
  };
  const handleClose = () => guardClose(doClose);

  useEffect(() => {
    if (error) {
      console.log(error);
      setErrorResponse(error.data.message);
    }
  }, [error]);

  useEffect(() => {
    if (!isLoading && !error) {
      doClose();
      setErrorResponse('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, error, dispatch]);

  const handleChange = (e) => {
    setErrorResponse('');
    setIsDirty(true);
    setForm({
      ...form,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async () => {
    const validationErrors = validateFormData(t, postValidationDocSchema, {
      ...form,
      date: selectedDate,
      file: selectedFile,
    });
    if (!!validationErrors) {
      setFormValidationErrors(validationErrors);
      return;
    }

    if (!neverExpires && !expiresAt) {
      setExpiresAtError(t('validation.expiresAtRequired'));
      return;
    }
    setExpiresAtError('');

    try {
      const formData = new FormData();
      formData.append('name', form.name);
      formData.append('description', form.description);
      formData.append('approvalDate', selectedDate);
      formData.append('machine', JSON.stringify({ id }));
      formData.append('file', selectedFile);
      if (form.type) formData.append('type', form.type);
      if (!neverExpires && expiresAt) formData.append('expiresAt', expiresAt);

      await registerValidDocument(formData);
      await fetchData();
      setForm(initialForm);
      setSelectedDate(null);
      setSelectedFile(null);
    } catch (err) {
      console.error('Error add document:', err);
    }
  };

  return (
    <div>
      <ModalButton
        variant="outlined"
        color="primary"
        onClick={handleOpen}
        style={{ padding: '5px 15px' }}
      >
        {t('modals.upload')}
      </ModalButton>
      <ModalComponent open={open} handleClose={handleClose}>
        <div className={styles.modal}>
          <DialogTitle sx={{ m: 0, p: 2 }}>{t('modals.addValidDocument')}</DialogTitle>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={(theme) => ({
              position: 'absolute',
              right: 8,
              top: 8,
              color: theme.palette.grey[500],
            })}
          >
            <CloseIcon />
          </IconButton>

          <DialogContent dividers sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                label={t('generic.name')}
                required
                variant="outlined"
                name="name"
                onChange={handleChange}
                fullWidth
                error={!!nameError}
                helperText={nameError ?? ' '}
                sx={commonTextFieldStyles}
                placeholder="e.g. ISO 9001 certificate"
              />
              <TextField
                label={t('generic.description')}
                variant="outlined"
                name="description"
                onChange={handleChange}
                fullWidth
                multiline
                minRows={3}
                error={!!descrError}
                helperText={descrError ?? ' '}
                sx={commonTextAreaStyles}
                placeholder="Optional notes about this document"
              />
              <TextField
                select
                required
                label={t('validation.type')}
                value={form.type}
                onChange={(e) => {
                  setIsDirty(true);
                  setForm({ ...form, type: e.target.value });
                }}
                fullWidth
                sx={commonTextFieldStyles}
              >
                {VALIDATION_TYPES.map((tp) => (
                  <MenuItem key={tp} value={tp}>
                    {t(`validation.${tp}`)}
                  </MenuItem>
                ))}
              </TextField>

              <Divider sx={{ my: 1 }} />
              <Typography variant="body2" color="text.secondary" fontWeight={500}>
                {t('validation.validityPeriod')}
              </Typography>

              <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="de">
                <DatePicker
                  label={t('validation.approvalAndValidFrom')}
                  value={selectedDate}
                  onChange={(newValue) => { setIsDirty(true); setSelectedDate(newValue); }}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      sx: commonTextFieldStyles,
                      error: !!dateError,
                      helperText: dateError || ' ',
                      required: true,
                    },
                    field: { clearable: true },
                  }}
                />
              </LocalizationProvider>

              <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="de">
                <DatePicker
                  label={t('validation.expiresAt')}
                  value={neverExpires ? null : expiresAt}
                  disabled={neverExpires}
                  onChange={(newValue) => {
                    setIsDirty(true);
                    setExpiresAt(newValue);
                    if (newValue) setExpiresAtError('');
                  }}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      sx: commonTextFieldStyles,
                      error: !!expiresAtError,
                      helperText: expiresAtError || ' ',
                    },
                    field: { clearable: true },
                  }}
                />
              </LocalizationProvider>

              <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  {t('validation.quickSet')}:
                </Typography>
                {[1, 2, 3, 4, 5].map((y) => (
                  <Button
                    key={y}
                    size="small"
                    variant="outlined"
                    onClick={() => applyExpiryPreset(y)}
                    disabled={neverExpires}
                    sx={{ textTransform: 'none', minWidth: 44 }}
                  >
                    +{y}y
                  </Button>
                ))}
              </Stack>

              <FormControlLabel
                control={
                  <Checkbox
                    checked={neverExpires}
                    onChange={(e) => {
                      setIsDirty(true);
                      setNeverExpires(e.target.checked);
                      if (e.target.checked) {
                        setExpiresAt(null);
                        setExpiresAtError('');
                      }
                    }}
                  />
                }
                label={t('validation.neverExpires')}
              />

              <Divider sx={{ my: 1 }} />
              <Typography variant="body2" color="text.secondary" fontWeight={500}>
                {t('validation.documentFile')} *
              </Typography>

              <DragDropFileUpload
                accept={INPUT_FILE_ACCEPT_DOC}
                selectedFile={selectedFile}
                onFileChange={(file, error) => {
                  setIsDirty(true);
                  if (error) {
                    setErrorResponse(error);
                    setSelectedFile(null);
                  } else {
                    setErrorResponse('');
                    setSelectedFile(file);
                  }
                }}
                onFileRemove={() => {
                  setIsDirty(true);
                  setSelectedFile(null);
                  setErrorResponse('');
                }}
                error={fileError}
                placeholder={t('modals.uploadDocument')}
              />
            </Box>

            {errorResponse && (
              <div style={{ marginTop: '16px' }}>
                {Array.isArray(errorResponse) ? (
                  errorResponse.map((item, index) => (
                    <Typography key={index} color="error">
                      {item}
                    </Typography>
                  ))
                ) : (
                  <Typography color="error">{errorResponse}</Typography>
                )}
              </div>
            )}
          </DialogContent>

          <ModalDialogActions>
            <ModalButton
              variant="outlined"
              color="primary"
              onClick={handleClose}
            >
              {t('modals.cancel')}
            </ModalButton>
            <ModalButton
              variant="contained"
              color="primary"
              onClick={handleSubmit}
              disabled={isLoading}
            >
              {isLoading ? t('generic.loading') : t('modals.add')}
            </ModalButton>
          </ModalDialogActions>
        </div>
      </ModalComponent>
      <ConfirmDiscardDialog {...confirmProps} />
    </div>
  );
}
